// SPDX-License-Identifier: AGPL-3.0-or-later

import i18n from '@app/app/I18n';
import {LongPressable} from '@app/features/app/components/LongPressable';
import {getStatusTypeLabel} from '@app/features/app/constants/AppConstants';
import * as PrivateChannelCommands from '@app/features/channel/commands/PrivateChannelCommands';
import Presence from '@app/features/presence/state/Presence';
import Relationships from '@app/features/relationship/state/Relationships';
import styles from '@app/features/relationship/utils/FriendsListUtils.module.css';
import * as QuickSwitcherCommands from '@app/features/search/commands/QuickSwitcherCommands';
import * as LayoutCommands from '@app/features/ui/commands/LayoutCommands';
import {Scroller} from '@app/features/ui/components/Scroller';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {RelationshipTypes} from '@app/vendor/constants/UserConstants';
import {msg} from '@lingui/core/macro';
import {CaretRightIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

interface FriendGroup {
	letter: string;
	friendIds: Array<string>;
}
interface FriendsListContentProps {
	variant?: 'sheet' | 'embedded';
	onBack?: () => void;
	className?: string;
	searchQuery?: string;
	onSearchChange?: (value: string) => void;
	showSearch?: boolean;
	showHeader?: boolean;
	onTotalCountChange?: (count: number) => void;
}
const useFriendGroups = (friendIds: Array<string>, searchQuery: string) => {
	return useMemo(() => {
		const filtered = friendIds.filter((userId) => {
			const user = Users.getUser(userId);
			if (!user) return false;
			if (!searchQuery) return true;
			const nickname = NicknameUtils.getNickname(user).toLowerCase();
			return nickname.includes(searchQuery.toLowerCase());
		});
		const groups: Record<string, Array<string>> = {};
		for (const userId of filtered) {
			const user = Users.getUser(userId);
			if (!user) continue;
			const firstLetter = NicknameUtils.getNickname(user)[0].toUpperCase();
			if (!groups[firstLetter]) {
				groups[firstLetter] = [];
			}
			groups[firstLetter].push(userId);
		}
		for (const letter of Object.keys(groups)) {
			groups[letter].sort((a, b) => {
				const userA = Users.getUser(a);
				const userB = Users.getUser(b);
				if (!userA || !userB) return 0;
				return NicknameUtils.getNickname(userA).localeCompare(NicknameUtils.getNickname(userB));
			});
		}
		const groupArray: Array<FriendGroup> = Object.keys(groups)
			.sort()
			.map((letter) => ({
				letter,
				friendIds: groups[letter],
			}));
		return groupArray;
	}, [friendIds, searchQuery]);
};
const FriendItem = observer(({userId}: {userId: string}) => {
	const user = Users.getUser(userId);
	const status = Presence.getStatus(userId);
	const handleClick = useCallback(async () => {
		try {
			await PrivateChannelCommands.openDMChannel(userId);
			if (MobileLayout.isMobileLayout()) {
				LayoutCommands.updateMobileLayoutState(false, true);
			}
			QuickSwitcherCommands.hide();
		} catch {}
	}, [userId]);
	const handleLongPress = useCallback(() => {
		UserProfileCommands.openUserProfile(userId);
	}, [userId]);
	if (!user) return null;
	const statusLabel = getStatusTypeLabel(i18n, status);
	return (
		<LongPressable className={styles.friendItemWrapper} onLongPress={handleLongPress}>
			<FocusRing offset={-2} enabled={false}>
				<button type="button" className={styles.friendItem} onClick={handleClick}>
					<div className={styles.friendItemContent}>
						<div className={styles.avatar}>
							<StatusAwareAvatar user={user} size={32} />
						</div>
						<div className={styles.friendItemText}>
							<div className={styles.friendItemName}>{NicknameUtils.getNickname(user)}</div>
							{statusLabel && <div className={styles.friendItemStatus}>{statusLabel}</div>}
						</div>
					</div>
					<CaretRightIcon weight="bold" className={styles.friendItemCaret} />
				</button>
			</FocusRing>
		</LongPressable>
	);
});
export const FriendsListContent: React.FC<FriendsListContentProps> = observer(
	({className, searchQuery, onTotalCountChange, variant}) => {
		const [internalSearchQuery, _setInternalSearchQuery] = useState('');
		const query = searchQuery ?? internalSearchQuery;
		const relationships = Relationships.getRelationships();
		const friendIds = relationships
			.filter((relation) => relation.type === RelationshipTypes.FRIEND)
			.map((relation) => relation.id);
		const groupedFriends = useFriendGroups(friendIds, query);
		const totalCount = groupedFriends.reduce((sum, group) => sum + group.friendIds.length, 0);
		useEffect(() => {
			onTotalCountChange?.(totalCount);
		}, [onTotalCountChange, totalCount]);
		const containerClassName = clsx(styles.container, variant === 'embedded' && styles.variantEmbedded, className);
		if (friendIds.length === 0) {
			return (
				<div className={containerClassName}>
					<div className={styles.emptyState}>
						<div className={styles.emptyStateTitle}>{i18n._(msg`No friends yet`)}</div>
						<div className={styles.emptyStateHint}>{i18n._(msg`Add some friends to see them here.`)}</div>
					</div>
				</div>
			);
		}
		if (query && totalCount === 0) {
			return (
				<div className={containerClassName}>
					<div className={styles.emptyState}>
						<div className={styles.emptyStateTitle}>{i18n._(msg`No friends match your search`)}</div>
						<div className={styles.emptyStateHint}>{i18n._(msg`Try another name or check your spelling.`)}</div>
					</div>
				</div>
			);
		}
		return (
			<div className={containerClassName}>
				<Scroller className={styles.scroller} key="friends-list-content-scroller">
					<div className={styles.scrollContent}>
						{groupedFriends.map((group) => (
							<div key={group.letter} className={styles.section}>
								<div className={styles.sectionHeader}>{group.letter}</div>
								<div className={styles.sectionList}>
									{group.friendIds.map((friendId) => (
										<FriendItem key={friendId} userId={friendId} />
									))}
								</div>
							</div>
						))}
					</div>
				</Scroller>
			</div>
		);
	},
);
